import { isDefined } from "@clipboard-health/util-ts";
import { get } from "@src/appV2/api";
import { environmentConfig } from "@src/appV2/environment";
import { useQuery, useQueryClient, type UseQueryOptions } from "@tanstack/react-query";

import { useCurrentUserId } from "../../lib/useCurrentUserId";
import {
  type Case,
  type CaseFilter,
  type CaseSort,
  type CasesParams,
  casesParamsSchema,
  casesResponseSchema,
} from "../types";
import { getCaseQueryKey } from "./useCase";
import { getPatientQueryKey } from "./usePatient";

export function getAgencyCasesQueryKey(params: {
  workplaceId: string;
  filter?: CaseFilter;
  sort?: CaseSort;
}) {
  const keyWithFilter = [
    "home-health",
    params.workplaceId,
    "cases",
    params.filter?.status,
    { ...params.filter },
  ];

  if (isDefined(params.sort)) {
    keyWithFilter.push(params.sort);
  }

  return keyWithFilter;
}

export function useAgencyCases(
  params: CasesParams,
  options: UseQueryOptions<Case[], unknown, Case[]> = {}
) {
  const { filter, sort } = params;

  const queryClient = useQueryClient();
  const workplaceId = useCurrentUserId() ?? "";

  return useQuery({
    queryKey: getAgencyCasesQueryKey({ workplaceId, filter, sort }),
    queryFn: async () => {
      const response = await get({
        url: `${environmentConfig.REACT_APP_HOME_HEALTH_API_URL}/v1/${workplaceId}/cases`,
        requestSchema: casesParamsSchema,
        responseSchema: casesResponseSchema,
        queryParams: { filter, sort },
      });

      return response.data.data;
    },
    onSuccess: (cases) => {
      cases.forEach((caseItem) => {
        queryClient.setQueryData(getCaseQueryKey({ workplaceId, caseId: caseItem.id }), caseItem);
        queryClient.setQueryData(
          getPatientQueryKey({ workplaceId, patientId: caseItem.attributes.patient.id }),
          caseItem.attributes.patient
        );
      });
    },
    retry: 0,
    staleTime: 1000 * 60 * 5,
    refetchInterval: 1000 * 60 * 10,
    ...options,
  });
}
